import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useState } from "react";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, EmptyState } from "@/components/common";
import { fDateTime } from "@/lib/format";
import { CalendarClock, Upload, Shuffle, CheckCircle2, Wand2, UploadCloud, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function CsvImport({ endpoint, label, placeholder, onDone }) {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const run = async () => {
    try { const r = await api.post(endpoint, { csv_text: text }); toast.success(t("import_done", { data: JSON.stringify(r.data) })); setText(""); setOpen(false); onDone?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid={`csv-${endpoint.split("/").pop()}`}><UploadCloud className="mr-1 h-4 w-4" />{label}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{label}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{placeholder}</p>
        <Textarea rows={8} className="font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} data-testid="csv-textarea" />
        <DialogFooter><Button onClick={run} data-testid="csv-run">{t("import_btn")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScenarioBox({ res }) {
  const { t } = useLang();
  return (
    <Card className="mt-4 p-4">
      <div className="mb-2 text-sm font-semibold">{t("staffing_suggest_for", { name: res.station.name })}</div>
      <div className="mb-3 flex flex-wrap gap-1 text-[11px]">
        {res.hard_rules.map((r) => <Badge key={r} variant="secondary">{r}</Badge>)}
      </div>
      {res.proposals.map((p) => (
        <div key={p.name} className="mb-3 rounded-lg border p-3">
          <div className="font-medium">{p.name}</div>
          <div className="text-xs text-muted-foreground">{p.rationale}</div>
          <ul className="mt-2 space-y-1 text-sm">
            {p.employees.length === 0 && <li className="text-muted-foreground">{t("no_candidates")}</li>}
            {p.employees.map((e) => (
              <li key={e.employee_id} className="flex items-center justify-between rounded bg-secondary/40 px-2 py-1">
                <span>{e.name}</span>
                <span className="font-mono text-xs">{t("score_heavy", { score: e.score, heavy: e.heavy_pct })}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {res.blocked.length > 0 && (
        <div className="text-xs text-muted-foreground">{t("blocked_prefix", { names: res.blocked.map((b) => b.name).join(", ") })}</div>
      )}
    </Card>
  );
}

export default function Schedule() {
  const { t } = useLang();
  const { effectiveRole } = useAuth();
  const canApprove = ["owner", "chef"].includes(effectiveRole);
  const { data: assignments, reload: rA } = useApi("/assignments");
  const { data: events, reload: rE } = useApi("/work-events");
  const { data: unplanned, reload: rU } = useApi("/rotation/unplanned");
  const { data: stations } = useApi("/stations");
  const { data: employees } = useApi("/employees");
  const { data: shifts } = useApi("/shifts");
  const [scenario, setScenario] = useState(null);
  const [sg, setSg] = useState({ station_id: "", start: "", end: "", shift_date: "" });
  const [np, setNp] = useState({ employee_id: "", station_id: "", shift_date: "", start: "", end: "" });
  const [npOpen, setNpOpen] = useState(false);
  const empName = (id) => { const e = (employees || []).find((x) => x.id === id); return e ? `${e.first_name} ${e.last_name}` : id; };
  const stName = (id) => (stations || []).find((x) => x.id === id)?.name || id;

  const createAssign = async () => {
    try {
      await api.post("/assignments", { ...np, status: "draft" });
      toast.success(t("assign_created"));
      setNp({ employee_id: "", station_id: "", shift_date: "", start: "", end: "" }); setNpOpen(false); rA();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const publish = async (id) => { try { await api.post(`/assignments/${id}/publish`); toast.success(t("published_toast")); rA(); } catch (e) { toast.error(apiErr(e)); } };
  const suggest = async () => {
    try { const r = await api.post("/staffing/suggest", sg); setScenario(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title={t("sched_title")} icon={CalendarClock} subtitle={t("sched_sub")} />
      <Tabs defaultValue="planerat">
        <TabsList>
          <TabsTrigger value="planerat" data-testid="tab-planerat">{t("tab_planned")}</TabsTrigger>
          <TabsTrigger value="faktiskt" data-testid="tab-faktiskt">{t("tab_actual")}</TabsTrigger>
          <TabsTrigger value="oplanerat" data-testid="tab-oplanerat">{t("tab_unplanned")} {(unplanned || []).length > 0 && <Badge variant="destructive" className="ml-1">{unplanned.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="forslag" data-testid="tab-forslag">{t("tab_suggest")}</TabsTrigger>
        </TabsList>

        <TabsContent value="planerat" className="mt-4">
          <div className="mb-3 flex flex-wrap justify-end gap-2">
            <Dialog open={npOpen} onOpenChange={setNpOpen}>
              <DialogTrigger asChild><Button data-testid="new-shift-btn"><Plus className="mr-1 h-4 w-4" />{t("new_shift")}</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("create_new_shift")}</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <div><Label>{t("lbl_employee")}</Label>
                    <Select value={np.employee_id} onValueChange={(v) => setNp((s) => ({ ...s, employee_id: v }))}>
                      <SelectTrigger data-testid="np-employee"><SelectValue placeholder={t("choose")} /></SelectTrigger>
                      <SelectContent>{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>{t("lbl_station")}</Label>
                    <Select value={np.station_id} onValueChange={(v) => setNp((s) => ({ ...s, station_id: v }))}>
                      <SelectTrigger data-testid="np-station"><SelectValue placeholder={t("choose")} /></SelectTrigger>
                      <SelectContent>{(stations || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>{t("shift_opt_lbl")}</Label>
                    <Select value={np.shift_id || "none"} onValueChange={(v) => setNp((s) => ({ ...s, shift_id: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid="np-shift"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">—</SelectItem>{(shifts || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("start_lbl")}</Label><Input type="datetime-local" value={np.start} onChange={(e) => setNp((s) => ({ ...s, start: e.target.value, shift_date: e.target.value.slice(0, 10) }))} data-testid="np-start" /></div>
                    <div><Label>{t("end_lbl")}</Label><Input type="datetime-local" value={np.end} onChange={(e) => setNp((s) => ({ ...s, end: e.target.value }))} data-testid="np-end" /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={createAssign} disabled={!np.employee_id || !np.station_id || !np.start} data-testid="save-shift">{t("create_shift_btn")}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <CsvImport endpoint="/assignments/import-csv" label={t("import_schedule")}
              placeholder={t("import_schedule_cols")} onDone={rA} />
          </div>
          {(assignments || []).length === 0 ? <EmptyState icon={CalendarClock} title={t("no_schedule")} text={t("no_schedule_text")} /> : (
            <Card className="overflow-x-auto kr-scroll">
              <Table>
                <TableHeader><TableRow><TableHead>{t("th_employee")}</TableHead><TableHead>{t("lbl_station")}</TableHead><TableHead>{t("th_start")}</TableHead><TableHead>{t("th_end")}</TableHead><TableHead>{t("th_status")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {assignments.map((a) => (
                    <TableRow key={a.id} data-testid={`assign-${a.id}`}>
                      <TableCell className="font-medium">{empName(a.employee_id)}</TableCell>
                      <TableCell>{stName(a.station_id)}</TableCell>
                      <TableCell className="font-mono text-xs">{fDateTime(a.start)}</TableCell>
                      <TableCell className="font-mono text-xs">{fDateTime(a.end)}</TableCell>
                      <TableCell><Badge variant={a.status === "published" ? "default" : "secondary"}>{a.status}</Badge></TableCell>
                      <TableCell>{canApprove && a.status !== "published" && <Button size="sm" onClick={() => publish(a.id)} data-testid={`publish-${a.id}`}>{t("approve_publish")}</Button>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="faktiskt" className="mt-4">
          <div className="mb-3 flex justify-end"><CsvImport endpoint="/work-events/import-csv" label={t("import_actual")}
            placeholder={t("import_actual_cols")} onDone={() => { rE(); rU(); }} /></div>
          {(events || []).length === 0 ? <EmptyState icon={Upload} title={t("no_events")} text={t("no_events_text")} /> : (
            <Card className="overflow-x-auto kr-scroll">
              <Table>
                <TableHeader><TableRow><TableHead>{t("th_event_id")}</TableHead><TableHead>{t("th_employee")}</TableHead><TableHead>{t("lbl_station")}</TableHead><TableHead>{t("th_task")}</TableHead><TableHead>{t("th_start")}</TableHead><TableHead>{t("th_type")}</TableHead><TableHead>{t("th_source")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {events.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono text-xs">{e.external_event_id}</TableCell>
                      <TableCell>{empName(e.employee_id)}</TableCell>
                      <TableCell>{stName(e.station_id)}</TableCell>
                      <TableCell>{e.task || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{fDateTime(e.start)}</TableCell>
                      <TableCell>{e.activity_type === "unplanned" ? <Badge variant="destructive">{t("type_unplanned")}</Badge> : <Badge variant="secondary">{t("type_planned")}</Badge>}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{e.source_system}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="oplanerat" className="mt-4">
          {(unplanned || []).length === 0 ? (
            <Card className="flex items-center gap-3 border-emerald-200 bg-emerald-50 p-5"><CheckCircle2 className="h-6 w-6 text-emerald-600" /><span className="font-medium text-emerald-800">{t("no_unplanned")}</span></Card>
          ) : (
            <div className="space-y-2">
              {unplanned.map((u) => (
                <Card key={u.work_event_id} className="flex items-start gap-3 border-amber-200 bg-amber-50/60 p-4" data-testid={`unplanned-${u.work_event_id}`}>
                  <Shuffle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div className="text-sm">
                    <div className="font-medium">{empName(u.employee_id)}</div>
                    <div className="text-muted-foreground">{u.reason}. {u.planned_station_id && t("planned_actual", { p: stName(u.planned_station_id), a: stName(u.actual_station_id) })}</div>
                    <div className="font-mono text-xs text-muted-foreground">{fDateTime(u.start)}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="forslag" className="mt-4">
          <Card className="p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Wand2 className="h-4 w-4 text-accent" />{t("gen_suggest")}</div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="sm:col-span-1"><Label>{t("lbl_station")}</Label>
                <Select value={sg.station_id} onValueChange={(v) => setSg((s) => ({ ...s, station_id: v }))}>
                  <SelectTrigger data-testid="sg-station"><SelectValue placeholder={t("choose")} /></SelectTrigger>
                  <SelectContent>{(stations || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>{t("lbl_date")}</Label><Input type="date" value={sg.shift_date} onChange={(e) => setSg((s) => ({ ...s, shift_date: e.target.value }))} /></div>
              <div><Label>{t("start_lbl")}</Label><Input type="datetime-local" value={sg.start} onChange={(e) => setSg((s) => ({ ...s, start: e.target.value }))} /></div>
              <div><Label>{t("end_lbl")}</Label><Input type="datetime-local" value={sg.end} onChange={(e) => setSg((s) => ({ ...s, end: e.target.value }))} /></div>
            </div>
            <Button className="mt-3" onClick={suggest} disabled={!sg.station_id} data-testid="generate-suggest">{t("give_suggest")}</Button>
            <p className="mt-2 text-xs text-muted-foreground">{t("suggest_note")}</p>
          </Card>
          {scenario && <ScenarioBox res={scenario} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
